import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreateProjectComment201,
  CreateProjectComment400,
  CreateProjectComment401,
  CreateProjectComment404,
  CreateProjectCommentBody,
  DeleteProject200,
  DeleteProject401,
  DeleteProject403,
  DeleteProject404,
  DeleteProjectComment200,
  DeleteProjectComment401,
  DeleteProjectComment403,
  DeleteProjectComment404,
  GetProjectById200,
  GetProjectById404,
  GetProjectComments200,
  GetProjectComments404,
  GetProjectCommentsParams,
  GetProjects200,
  GetProjects400,
  GetProjects401,
  GetProjects500,
  GetProjectsParams,
  LikeProject200,
  LikeProject401,
  LikeProject404,
  SaveProject201,
  SaveProjectBody,
  UnlikeProject200,
  UnlikeProject401,
  UnlikeProject404
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 프로젝트 메타데이터(JSON)와 모든 파일(프로젝트 ZIP + 미디어 파일들)을 별도 파트로 전송하여 저장합니다.
파일 배열의 첫 번째 요소는 프로젝트 ZIP 파일이어야 합니다.
 * @summary 프로젝트 저장
 */
export const saveProject = (
    saveProjectBody: SaveProjectBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<SaveProject201>> => {const formData = new FormData();
formData.append('metadata', JSON.stringify(saveProjectBody.metadata));
saveProjectBody.files.forEach(value => formData.append('files', value));

    
    return axios.post(
      `https://api.ugot.uk/api/projects`,
      formData,options
    );
  }



export const getSaveProjectMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveProject>>, TError,{data: SaveProjectBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof saveProject>>, TError,{data: SaveProjectBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveProject>>, {data: SaveProjectBody}> = (props) => {
          const {data} = props ?? {};

          return  saveProject(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type SaveProjectMutationResult = NonNullable<Awaited<ReturnType<typeof saveProject>>>
    export type SaveProjectMutationBody = SaveProjectBody
    export type SaveProjectMutationError = AxiosError<void>

    /**
 * @summary 프로젝트 저장
 */
export const useSaveProject = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveProject>>, TError,{data: SaveProjectBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof saveProject>>,
        TError,
        {data: SaveProjectBody},
        TContext
      > => {

      const mutationOptions = getSaveProjectMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 다양한 조건으로 프로젝트를 조회합니다. 검색 조건 없이 호출할 경우 기본 목록(추천 등)을 조회합니다.

## 주요 조건
- **type**: 조회 관점/맥락 (FOR_YOU, USER, FOLLOW, DETAIL)
- **status**: 프로젝트 상태 (ALL, PUBLISHED, DRAFT)
- **query**: 키워드 검색 (제목, 설명)
- **tag**: 태그 검색
- **userId**: 특정 사용자의 프로젝트 조회 (type=USER 와 함께 사용)
- **startDate/endDate**: 날짜 범위 지정
# TODO: 만약 프로젝트 카테고리(GAME, APP 등) 필터링이 필요하다면 별도 파라미터(category) 추가 필요

## 정렬 옵션
- **sortBy**: 정렬 기준 (createdAt, updatedAt, views, likes, comments)
- **sortDirection**: 정렬 방향 (ASC, DESC)

 * @summary 프로젝트 목록 조회
 */
export const getProjects = (
    params?: GetProjectsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetProjects200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/projects`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetProjectsQueryKey = (params?: GetProjectsParams,) => {
    return [`https://api.ugot.uk/api/projects`, ...(params ? [params]: [])] as const;
    }

    
export const getGetProjectsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getProjects>>, TError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>>(params?: GetProjectsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjects>>> = ({ signal }) => getProjects(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getProjects>>>
export type GetProjectsInfiniteQueryError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>

/**
 * @summary 프로젝트 목록 조회
 */
export const useGetProjectsInfinite = <TData = Awaited<ReturnType<typeof getProjects>>, TError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>>(
 params?: GetProjectsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetProjectsQueryOptions = <TData = Awaited<ReturnType<typeof getProjects>>, TError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>>(params?: GetProjectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjects>>> = ({ signal }) => getProjects(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectsQueryResult = NonNullable<Awaited<ReturnType<typeof getProjects>>>
export type GetProjectsQueryError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>

/**
 * @summary 프로젝트 목록 조회
 */
export const useGetProjects = <TData = Awaited<ReturnType<typeof getProjects>>, TError = AxiosError<GetProjects400 | GetProjects401 | GetProjects500>>(
 params?: GetProjectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 프로젝트 상세 정보를 조회합니다.
 * @summary 프로젝트 상세 조회
 */
export const getProjectById = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetProjectById200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/projects/${id}`,options
    );
  }


export const getGetProjectByIdQueryKey = (id: number,) => {
    return [`https://api.ugot.uk/api/projects/${id}`] as const;
    }

    
export const getGetProjectByIdInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getProjectById>>, TError = AxiosError<GetProjectById404>>(id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectById>>> = ({ signal }) => getProjectById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectByIdInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectById>>>
export type GetProjectByIdInfiniteQueryError = AxiosError<GetProjectById404>

/**
 * @summary 프로젝트 상세 조회
 */
export const useGetProjectByIdInfinite = <TData = Awaited<ReturnType<typeof getProjectById>>, TError = AxiosError<GetProjectById404>>(
 id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectByIdInfiniteQueryOptions(id,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetProjectByIdQueryOptions = <TData = Awaited<ReturnType<typeof getProjectById>>, TError = AxiosError<GetProjectById404>>(id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectById>>> = ({ signal }) => getProjectById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectById>>>
export type GetProjectByIdQueryError = AxiosError<GetProjectById404>

/**
 * @summary 프로젝트 상세 조회
 */
export const useGetProjectById = <TData = Awaited<ReturnType<typeof getProjectById>>, TError = AxiosError<GetProjectById404>>(
 id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectByIdQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 프로젝트를 삭제합니다.
 * @summary 프로젝트 삭제
 */
export const deleteProject = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<DeleteProject200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/projects/${id}`,options
    );
  }



export const getDeleteProjectMutationOptions = <TError = AxiosError<DeleteProject401 | DeleteProject403 | DeleteProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteProject>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteProject>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  deleteProject(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteProjectMutationResult = NonNullable<Awaited<ReturnType<typeof deleteProject>>>
    
    export type DeleteProjectMutationError = AxiosError<DeleteProject401 | DeleteProject403 | DeleteProject404>

    /**
 * @summary 프로젝트 삭제
 */
export const useDeleteProject = <TError = AxiosError<DeleteProject401 | DeleteProject403 | DeleteProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteProject>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getDeleteProjectMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 프로젝트에 좋아요를 표시합니다.
 * @summary 프로젝트 좋아요
 */
export const likeProject = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<LikeProject200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/projects/${id}/like`,undefined,options
    );
  }



export const getLikeProjectMutationOptions = <TError = AxiosError<LikeProject401 | LikeProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof likeProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof likeProject>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof likeProject>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  likeProject(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type LikeProjectMutationResult = NonNullable<Awaited<ReturnType<typeof likeProject>>>
    
    export type LikeProjectMutationError = AxiosError<LikeProject401 | LikeProject404>

    /**
 * @summary 프로젝트 좋아요
 */
export const useLikeProject = <TError = AxiosError<LikeProject401 | LikeProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof likeProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof likeProject>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getLikeProjectMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 프로젝트에 표시한 좋아요를 취소합니다.
 * @summary 프로젝트 좋아요 취소
 */
export const unlikeProject = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UnlikeProject200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/projects/${id}/like`,options
    );
  }



export const getUnlikeProjectMutationOptions = <TError = AxiosError<UnlikeProject401 | UnlikeProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlikeProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof unlikeProject>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlikeProject>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  unlikeProject(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UnlikeProjectMutationResult = NonNullable<Awaited<ReturnType<typeof unlikeProject>>>
    
    export type UnlikeProjectMutationError = AxiosError<UnlikeProject401 | UnlikeProject404>

    /**
 * @summary 프로젝트 좋아요 취소
 */
export const useUnlikeProject = <TError = AxiosError<UnlikeProject401 | UnlikeProject404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlikeProject>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof unlikeProject>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getUnlikeProjectMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 프로젝트에 작성된 댓글 목록을 조회합니다. parentId 파라미터 유무에 따라 루트 댓글 또는 답글 목록을 반환합니다.
 * @summary 프로젝트 댓글 목록 조회
 */
export const getProjectComments = (
    id: number,
    params?: GetProjectCommentsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetProjectComments200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/projects/${id}/comments`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetProjectCommentsQueryKey = (id: number,
    params?: GetProjectCommentsParams,) => {
    return [`https://api.ugot.uk/api/projects/${id}/comments`, ...(params ? [params]: [])] as const;
    }

    
export const getGetProjectCommentsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getProjectComments>>, TError = AxiosError<GetProjectComments404>>(id: number,
    params?: GetProjectCommentsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectCommentsQueryKey(id,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectComments>>> = ({ signal }) => getProjectComments(id,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectCommentsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectComments>>>
export type GetProjectCommentsInfiniteQueryError = AxiosError<GetProjectComments404>

/**
 * @summary 프로젝트 댓글 목록 조회
 */
export const useGetProjectCommentsInfinite = <TData = Awaited<ReturnType<typeof getProjectComments>>, TError = AxiosError<GetProjectComments404>>(
 id: number,
    params?: GetProjectCommentsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectCommentsInfiniteQueryOptions(id,params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetProjectCommentsQueryOptions = <TData = Awaited<ReturnType<typeof getProjectComments>>, TError = AxiosError<GetProjectComments404>>(id: number,
    params?: GetProjectCommentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectCommentsQueryKey(id,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectComments>>> = ({ signal }) => getProjectComments(id,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProjectCommentsQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectComments>>>
export type GetProjectCommentsQueryError = AxiosError<GetProjectComments404>

/**
 * @summary 프로젝트 댓글 목록 조회
 */
export const useGetProjectComments = <TData = Awaited<ReturnType<typeof getProjectComments>>, TError = AxiosError<GetProjectComments404>>(
 id: number,
    params?: GetProjectCommentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProjectComments>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetProjectCommentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 프로젝트에 댓글을 작성합니다.
 * @summary 프로젝트 댓글 작성
 */
export const createProjectComment = (
    id: number,
    createProjectCommentBody: CreateProjectCommentBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreateProjectComment201>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/projects/${id}/comments`,
      createProjectCommentBody,options
    );
  }



export const getCreateProjectCommentMutationOptions = <TError = AxiosError<CreateProjectComment400 | CreateProjectComment401 | CreateProjectComment404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProjectComment>>, TError,{id: number;data: CreateProjectCommentBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createProjectComment>>, TError,{id: number;data: CreateProjectCommentBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createProjectComment>>, {id: number;data: CreateProjectCommentBody}> = (props) => {
          const {id,data} = props ?? {};

          return  createProjectComment(id,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateProjectCommentMutationResult = NonNullable<Awaited<ReturnType<typeof createProjectComment>>>
    export type CreateProjectCommentMutationBody = CreateProjectCommentBody
    export type CreateProjectCommentMutationError = AxiosError<CreateProjectComment400 | CreateProjectComment401 | CreateProjectComment404>

    /**
 * @summary 프로젝트 댓글 작성
 */
export const useCreateProjectComment = <TError = AxiosError<CreateProjectComment400 | CreateProjectComment401 | CreateProjectComment404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProjectComment>>, TError,{id: number;data: CreateProjectCommentBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createProjectComment>>,
        TError,
        {id: number;data: CreateProjectCommentBody},
        TContext
      > => {

      const mutationOptions = getCreateProjectCommentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 프로젝트 댓글을 삭제합니다.
 * @summary 프로젝트 댓글 삭제
 */
export const deleteProjectComment = (
    id: number,
    commentId: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<DeleteProjectComment200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/projects/${id}/comments/${commentId}`,options
    );
  }



export const getDeleteProjectCommentMutationOptions = <TError = AxiosError<DeleteProjectComment401 | DeleteProjectComment403 | DeleteProjectComment404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProjectComment>>, TError,{id: number;commentId: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteProjectComment>>, TError,{id: number;commentId: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteProjectComment>>, {id: number;commentId: string}> = (props) => {
          const {id,commentId} = props ?? {};

          return  deleteProjectComment(id,commentId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteProjectCommentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteProjectComment>>>
    
    export type DeleteProjectCommentMutationError = AxiosError<DeleteProjectComment401 | DeleteProjectComment403 | DeleteProjectComment404>

    /**
 * @summary 프로젝트 댓글 삭제
 */
export const useDeleteProjectComment = <TError = AxiosError<DeleteProjectComment401 | DeleteProjectComment403 | DeleteProjectComment404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProjectComment>>, TError,{id: number;commentId: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteProjectComment>>,
        TError,
        {id: number;commentId: string},
        TContext
      > => {

      const mutationOptions = getDeleteProjectCommentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    